import { useEffect, useCallback, useState, useContext } from "react";
import { useForm } from "react-hook-form";
import { useTheme } from "@mui/material";
import ClientDetailUI from "./ClientDetailUI";
import { clientService } from "@services/ClientService";
import { transactionService } from "@services/TransactionService";
import { authService } from "@services/authService";
import { IClient, IClientSummary, IDataPage, ITableRow, ITransaction } from "src/types/global";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalAlertContext } from "@providers/GlobalAlertProvider";
import { createHeader, createTransactionRow, formatDate, getNumberCurrency } from "@utils/utils";
const ClientDetail = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [client, setClient] = useState<IClient>({
    id: -1,
    fullName: "",
    identification: "N/A",
    phone: "",
    email: "",
    creationTime: ""
  });
  const [summary, setSummary] = useState<IClientSummary>({
    totalRequested: 0,
    totalReturned: 0,
    totalPaid: 0,
    consumedAmount: 0,
    isOverCap: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isTransLoading, setIsTransLoading] = useState(true);
  const [isPayLoading, setIsPayLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Lo sentimos, no se pudo eliminar este cliente");
  const [showError, setShowError] = useState(false);
  const [showDialogDelete, setShowDialogDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [transactions, setTransactions] = useState<IDataPage>({});
  const [rows, setRows] = useState<ITableRow[]>([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [employeeCode, setEmployeeCode] = useState("");
  const [applicant, setApplicant] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(0);
  const [transferProof, setTransferProof] = useState<File | null>(null);
  const {
    register,
    setValue,
    handleSubmit,
    control
  } = useForm<ITransaction>();
  const {
    setGlobalAlertMessage,
    setGlobalAlertSeverity,
    showGlobalAlert,
    hiddenGlobalAlert
  } = useContext(GlobalAlertContext);
  const rowsPerPage = 8;
  const handleClosePayDialog = () => {
    setShowPayDialog(false);
    setEmployeeCode("");
    setApplicant("");
    setPaymentMethod(0);
    setTransferProof(null);
    setValue("amount", "");
    setValue("employeeCode", "");
    setValue("applicant", "");
  };
  const pay = async (data: ITransaction) => {
    try {
      setIsPayLoading(true);
      if (!employeeCode) {
        throw new Error("El código de empleado es requerido");
      }
      if (paymentMethod === 1 && !transferProof) {
        throw new Error("Se requiere el comprobante de transferencia");
      }
      data.transactionType = 1;
      data.amount = getNumberCurrency(String(data.amount));
      data.clientId = client.id;
      data.employeeCode = employeeCode;
      data.applicant = applicant;
      data.paymentMethod = paymentMethod;
      data.transferProof = transferProof ?? undefined;
      await transactionService.create(data);
      getSummary(client.id);
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("Abono registrado correctamente");
      showGlobalAlert();
    } catch (error) {
      setErrorMessage("Error, intentalo de nuevo");
      setShowError(true);
    } finally {
      handleClosePayDialog();
      setIsPayLoading(false);
    }
  };
  const handleDelete = async () => {
    try {
      setGlobalAlertSeverity("success");
      setGlobalAlertMessage("!Listo! El cliente ha sido eliminado correctamente.");
      setIsDeleting(true);
      setShowDialogDelete(false);
      await clientService.remove(client.id);
      navigate("/clientes", {
        state: {
          showAlert: true
        }
      });
      showGlobalAlert();
    } catch (error) {
      hiddenGlobalAlert();
      setErrorMessage("Lo sentimos, no se pudo eliminar este cliente");
      setShowError(true);
    } finally {
      setIsDeleting(false);
    }
  };
  const getTransactions = useCallback(async (id: number, skipPage = 0) => {
    try {
      if (id === -1) {
        return;
      }
      setIsTransLoading(true);
      const res = await transactionService.getTransactions(id, rowsPerPage, skipPage * rowsPerPage);
      setPages(Math.ceil(res.data.totalCount / rowsPerPage));
      setTransactions(t => ({
        ...t,
        [skipPage + 1]: {
          search: "",
          items: [...res.data.items.map(newT => {
            return createTransactionRow(newT.amount, newT.transactionType, newT.receiptId, newT.creationTime, theme.palette.success.main, theme.palette.error.main, newT.employeeName, newT.applicant, newT.id, newT.paymentMethod, newT.transferProofPath, () => {
              setErrorMessage("Error al descargar el comprobante");
              setShowError(true);
            });
          })]
        }
      }));
    } catch (error) {
      setErrorMessage("Error obteniendo movimientos");
      setShowError(true);
    } finally {
      setIsTransLoading(false);
    }
  }, [theme.palette]);
  const getSummary = useCallback(async (id: number) => {
    try {
      if (id === -1) {
        return;
      }
      const summaryRes = await clientService.getSummary(id);
      setSummary(summaryRes.data);
    } catch (error) {
      setErrorMessage("Error obteniendo movimientos");
      setShowError(true);
    }
  }, []);
  const getClient = useCallback(async (id: number, withState: boolean = false) => {
    try {
      if (client.id === id) {
        return;
      }
      if (!withState) {
        setIsLoading(true);
      }
      const res = await clientService.get(id);
      const clientDB = res.data;
      setClient({
        ...clientDB,
        creationTime: formatDate(clientDB.creationTime)
      });
    } catch (error) {
      setGlobalAlertSeverity("error");
      setGlobalAlertMessage("El cliente no existe");
      navigate("/clientes", {
        state: {
          showAlert: true
        }
      });
      setTimeout(() => {
        showGlobalAlert();
      }, 300);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, setGlobalAlertSeverity, setGlobalAlertMessage, showGlobalAlert, client.id]);
  useEffect(() => {
    const id = Number(location.pathname.split("/").reverse()[0]);
    const routeState: IClient = location.state?.client;
    if (id === routeState?.id) {
      setClient(routeState);
      return setIsLoading(false);
    }
    getClient(id, id === routeState?.id);
  }, [getClient, location]);
  useEffect(() => {
    if (!isPayLoading) {
      getTransactions(client.id);
      getSummary(client.id);
    }
  }, [client.id, getTransactions, isPayLoading, getSummary]);
  useEffect(() => {
    setRows([]);
    const data = transactions[currentPage];
    if (data) {
      setRows(data.items);
    } else {
      getTransactions(client.id, currentPage - 1);
    }
  }, [client.id, currentPage, getTransactions, transactions]);
  const headers = [createHeader("Cantidad", "amount"), createHeader("Tipo de movimiento", "transaction"), createHeader("Tipo de abono", "paymentMethod"), createHeader("Recibo", "receipt"), createHeader("Autor", "employeeName"), createHeader("Solicitante", "applicant"), createHeader("Fecha", "creationTime"), createHeader("Comprobante", "proof")];
  return <ClientDetailUI id={client.id} fullName={client.fullName} identification={client.identification} phone={client.phone} email={client.email} creationTime={client.creationTime} generatedPassword={client.generatedPassword} totalPaid={summary.totalPaid} totalRequested={summary.totalRequested} totalReturned={summary.totalReturned} moneyCapAmount={client.moneyCapAmount} consumedAmount={summary.consumedAmount} availableCapAmount={summary.availableCapAmount} isOverCap={summary.isOverCap} isLoading={isLoading} isTransLoading={isTransLoading} showError={showError} closeError={() => setShowError(false)} isDeleting={isDeleting} showDialog={showDialogDelete} handleConfirmDialog={handleDelete} handleOpenDialog={() => setShowDialogDelete(true)} handleCloseDialog={() => setShowDialogDelete(false)} headers={headers} rows={rows} pages={pages} onChangePage={setCurrentPage} emptyMessage={"Sin movimientos realizados"} showPayDialog={showPayDialog} setShowPayDialog={setShowPayDialog} pay={handleSubmit(pay)} register={register} isPayLoading={isPayLoading} errorMessage={errorMessage} isAdmin={authService.isAdmin()} employeeCode={employeeCode} setEmployeeCode={setEmployeeCode} applicant={applicant} setApplicant={setApplicant} handleClosePayDialog={handleClosePayDialog} control={control} paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} transferProof={transferProof} setTransferProof={setTransferProof} />;
};
export default ClientDetail;